import { useState, useEffect, useMemo } from "react";
import { useWasm } from "./useWasm";
import { parseTimeToday } from "@/lib/format";
import type { GeoCoordinates } from "@/types/location";
import type { PrayerTimes, PrayerName, NextPrayer } from "@/types/prayer";

interface UsePrayerTimesReturn {
  times: PrayerTimes | null;
  nextPrayer: NextPrayer | null;
  loading: boolean;
}

const PRAYER_NAMES: PrayerName[] = [
  "Fajr",
  "Sunrise",
  "Dhuhr",
  "Asr",
  "Maghrib",
  "Isha",
];

const PRAYER_KEYS = ["fajr", "sunrise", "dhuhr", "asr", "maghrib", "isha"] as const;

type PrayerKey = typeof PRAYER_KEYS[number];
type PrayerStrings = Record<PrayerKey, string>;

export function usePrayerTimes(
  coords: GeoCoordinates | null,
  method: string,
  asrMethod: string,
  date: Date
): UsePrayerTimesReturn {
  const { module, ready } = useWasm();
  const [prayerStrings, setPrayerStrings] = useState<PrayerStrings | null>(null);
  const [nextPrayer, setNextPrayer] = useState<NextPrayer | null>(null);

  // Compute the day's prayer time strings when location, method, or date change.
  useEffect(() => {
    if (!ready || !module || !coords) return;

    const utcOffset = -date.getTimezoneOffset() / 60;

    const result = module.calculatePrayerTimes(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate(),
      coords.latitude,
      coords.longitude,
      0, // elevation
      method,
      asrMethod,
      utcOffset
    );

    setPrayerStrings({
      fajr: result.fajr,
      sunrise: result.sunrise,
      dhuhr: result.dhuhr,
      asr: result.asr,
      maghrib: result.maghrib,
      isha: result.isha,
    });
  }, [ready, module, coords, method, asrMethod, date]);

  // Re-evaluate the next prayer once per second so the countdown advances when
  // a prayer time passes. A 1-second polling tick is more robust than a single
  // long setTimeout, which mobile browsers (especially iOS PWAs) can throttle
  // or suspend even while the page is in the foreground.
  useEffect(() => {
    if (!prayerStrings) return;

    function recompute() {
      const now = new Date();
      const prayerDates = PRAYER_KEYS.map((key) =>
        parseTimeToday(prayerStrings![key])
      );
      const nextIdx = prayerDates.findIndex((t) => t > now);

      let next: NextPrayer;
      if (nextIdx === -1) {
        // All of today's prayers have passed — count down to tomorrow's Fajr.
        const tomorrowFajr = new Date(prayerDates[0]);
        tomorrowFajr.setDate(tomorrowFajr.getDate() + 1);
        next = { name: "Fajr", time: tomorrowFajr };
      } else {
        next = { name: PRAYER_NAMES[nextIdx], time: prayerDates[nextIdx] };
      }

      // Preserve the previous reference when nothing changed so downstream
      // effects (e.g. useCountdown) don't re-run on every tick.
      setNextPrayer((prev) => {
        if (
          prev !== null &&
          prev.name === next.name &&
          prev.time.getTime() === next.time.getTime()
        ) {
          return prev;
        }
        return next;
      });
    }

    recompute();
    const interval = setInterval(recompute, 1000);
    return () => clearInterval(interval);
  }, [prayerStrings]);

  const times = useMemo<PrayerTimes | null>(() => {
    if (!prayerStrings) return null;
    return {
      Fajr: prayerStrings.fajr,
      Sunrise: prayerStrings.sunrise,
      Dhuhr: prayerStrings.dhuhr,
      Asr: prayerStrings.asr,
      Maghrib: prayerStrings.maghrib,
      Isha: prayerStrings.isha,
    };
  }, [prayerStrings]);

  return { times, nextPrayer, loading: !ready };
}
